/**
 * BookingConfirmationPage ✦
 *
 * Read-only public confirmation surface at /book/confirmation/:token.
 * Reached from the supplier sending the link by email/manual share —
 * NEVER an editable acceptance flow. Just the night, the supplier,
 * the package, and the terms the client agreed to.
 *
 * Calm walnut/charcoal editorial composition matching the rest of
 * the booking flow. Backend (`GET /api/book/confirmation/:token`)
 * tracks first-view via `confirmation_viewed_at` so the supplier
 * sees "viewed · <date>" in their ledger without polling.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_BG = '#0C0804';
const SHELL_BG = '#1A1208';
const RULE = 'rgba(194,157,82,0.22)';
const RULE_SOFT = 'rgba(194,157,82,0.14)';
const CREAM = '#F4E9D2';
const CREAM_MUTED = 'rgba(244,233,210,0.62)';
const GOLD = '#C29D52';

const Eyebrow = ({ children }) => (
  <p style={{
    margin: 0, fontFamily: 'Arial, sans-serif', fontSize: 10,
    fontWeight: 700, letterSpacing: '0.32em', textTransform: 'uppercase',
    color: GOLD,
  }}>{children}</p>
);

const Heading = ({ children }) => (
  <h1 style={{
    margin: '14px 0 0', fontFamily: 'Georgia, serif', fontWeight: 500,
    fontSize: 30, letterSpacing: '-0.012em', lineHeight: 1.18, color: CREAM,
  }}>{children}</h1>
);

const Row = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ marginTop: 18 }}>
      <p style={{
        margin: 0, fontFamily: 'Arial, sans-serif', fontSize: 10,
        fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase',
        color: GOLD,
      }}>{label}</p>
      <p style={{
        margin: '4px 0 0', fontFamily: 'Georgia, serif', fontSize: 15,
        color: CREAM, lineHeight: 1.55,
      }}>{value}</p>
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('en-GB', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const BookingConfirmationPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/book/confirmation/${token}`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.status === 404
              ? 'This confirmation could not be found.'
              : 'Could not load this confirmation. Try again shortly.'
          );
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const pdfUrl = `${API}/book/${token}/confirmation.pdf`;

  return (
    <div
      data-testid="booking-confirmation-page"
      style={{
        minHeight: '100vh', background: PAGE_BG, color: CREAM,
        fontFamily: 'Georgia, serif',
        padding: 'clamp(28px, 6vw, 64px) 16px',
      }}
    >
      <div style={{
        maxWidth: 640, margin: '0 auto',
        background: SHELL_BG, border: `1px solid ${RULE}`,
        borderRadius: 6,
        padding: 'clamp(28px, 5vw, 44px) clamp(24px, 5vw, 40px)',
      }}>
        {error && (
          <div data-testid="confirmation-error">
            <Eyebrow>✦ EventDrop</Eyebrow>
            <Heading>This confirmation is no longer here ✦</Heading>
            <p style={{ marginTop: 20, color: CREAM_MUTED, fontSize: 15, lineHeight: 1.6 }}>
              {error}
            </p>
          </div>
        )}

        {!error && !data && (
          <p data-testid="confirmation-loading" style={{ color: CREAM_MUTED, fontStyle: 'italic' }}>
            Opening the register…
          </p>
        )}

        {data && (
          <>
            <div style={{ width: 42, height: 1, background: GOLD, marginBottom: 18 }} />
            <Eyebrow>✦ A settled booking</Eyebrow>
            <Heading>Your night with {data.supplier_name} ✦</Heading>

            <p style={{
              marginTop: 18, color: CREAM_MUTED, fontSize: 15, lineHeight: 1.62,
            }}>
              Your booking is recorded and resting quietly in your supplier's diary.
              Keep this confirmation for your records.
            </p>

            {/* Quiet badge for supplier-created bookings — keeps the
                client softly aware that the supplier composed this on
                their behalf. */}
            {data.booking_origin === 'supplier' && (
              <p
                data-testid="confirmation-origin-supplier"
                style={{
                  marginTop: 14, fontFamily: 'Arial, sans-serif', fontSize: 10,
                  fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: GOLD,
                }}
              >
                Created quietly by {data.supplier_name}
              </p>
            )}

            <div style={{ marginTop: 20, paddingTop: 8, borderTop: `1px solid ${RULE_SOFT}` }}>
              <Row label="Package" value={data.package_name} />
              <Row label="Event date" value={fmtDate(data.event_date)} />
              <Row label="Time" value={data.event_time} />
              <Row label="Event type" value={data.event_type} />
              <Row label="Venue" value={data.venue} />
              <Row label="Venue address" value={data.venue_address} />
              <Row label="Access notes" value={data.access_issues} />
              <Row label="For" value={data.client_name} />
              <Row label="Client email" value={data.client_email} />
              <Row label="Client mobile" value={data.client_mobile} />
            </div>

            {data.supplier_note && (
              <div style={{
                marginTop: 28, paddingTop: 20, borderTop: `1px solid ${RULE_SOFT}`,
              }}>
                <Eyebrow>✦ A note from your supplier</Eyebrow>
                <p style={{
                  marginTop: 10, color: CREAM, fontSize: 15.5, lineHeight: 1.62,
                  fontStyle: 'italic',
                }}>
                  {data.supplier_note}
                </p>
              </div>
            )}

            {data.terms_text && (
              <div style={{
                marginTop: 28, paddingTop: 20, borderTop: `1px solid ${RULE_SOFT}`,
              }}>
                <Eyebrow>✦ Terms agreed</Eyebrow>
                <pre style={{
                  marginTop: 12, color: CREAM_MUTED, fontFamily: 'Georgia, serif',
                  fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                  background: 'transparent', border: 'none', padding: 0,
                }}>
                  {data.terms_text}
                </pre>
              </div>
            )}

            {/* Actions */}
            <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="confirmation-download-pdf"
                style={{
                  display: 'block', textAlign: 'center', textDecoration: 'none',
                  padding: '13px 26px', background: GOLD, color: '#1A1208',
                  borderRadius: 3, fontFamily: 'Georgia, serif', fontStyle: 'italic',
                  fontSize: 15,
                }}
              >
                Download confirmation ✦
              </a>
            </div>

            {/* Soft homepage-return — same editorial sentence as the
                client-completion success surface. The booking remains
                the primary outcome; this is discovery only. */}
            <div
              data-testid="confirmation-soft-return"
              style={{
                marginTop: 36, paddingTop: 22, borderTop: `1px solid ${RULE}`,
                fontFamily: 'Georgia, serif', fontSize: 14, lineHeight: 1.65,
                color: CREAM_MUTED, fontStyle: 'italic',
              }}
            >
              Your night now has a place to gather ✦{' '}
              <a
                href="/"
                data-testid="confirmation-explore-link"
                style={{
                  color: GOLD, textDecoration: 'underline',
                  textUnderlineOffset: '3px', fontStyle: 'normal',
                }}
              >
                Explore the night further ✦
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
